/**
 * Bridge Card Financial Chart
 * Shows Actual vs Ideal (revenue/expense) using correction factors, plus Future projection.
 * Future values are computed from REM_future and EEM_future (change in correction factor).
 * @feature BRIDGE_CARD - Business objective calculations with actual correction factors
 */

import React from 'react';
import type { BridgeCardObjective } from '../../types/bridgeCard';
import { ActualVsIdealChart } from '../BridgeSession/ActualVsIdealChart';
import type { EntropyMapMetrics } from '../../types/bridgeSession';
import styles from './BridgeCardFinancialChart.module.css';

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export interface BridgeCardFinancialChartProps {
  objective: BridgeCardObjective;
}

export const BridgeCardFinancialChart: React.FC<BridgeCardFinancialChartProps> = ({ objective }) => {
  const hasMetrics =
    (objective.current_revenue ?? 0) > 0 ||
    (objective.current_expense ?? 0) > 0;

  if (!hasMetrics) return null;

  const metrics: EntropyMapMetrics = {
    current_revenue: objective.current_revenue ?? 0,
    current_expense: objective.current_expense ?? 0,
    ideal_revenue: objective.ideal_revenue ?? (objective.current_revenue ?? 0) + (objective.recoverable_revenue ?? 0),
    ideal_expense: objective.ideal_expense ?? Math.max(0, (objective.current_expense ?? 0) - (objective.recoverable_expense ?? 0)),
    recoverable_revenue: objective.recoverable_revenue ?? 0,
    recoverable_expense: objective.recoverable_expense ?? 0,
    rem_current: objective.rem_current,
    eem_current: objective.eem_current,
    future_revenue: objective.future_revenue,
    future_expense: objective.future_expense,
  };

  const hasFutureProjection =
    (objective.future_revenue != null && objective.future_revenue > 0) ||
    (objective.future_expense != null && objective.future_expense > 0) ||
    objective.rem_future != null ||
    objective.eem_future != null;

  return (
    <div className={styles.chartWrap}>
      <ActualVsIdealChart metrics={metrics} showFutureArrows={hasFutureProjection} />
      {hasFutureProjection && (
        <div className={styles.futureSection}>
          <div className={styles.sectionLabel}>Future projection</div>
          <div className={styles.futureContent}>
            <div className={styles.futureBars}>
              <div className={styles.futureRow}>
                <span className={styles.futureLabel}>Revenue</span>
                <span className={styles.futureValue}>
                  {objective.future_revenue != null ? formatCurrency(objective.future_revenue) : '—'}
                </span>
              </div>
              <div className={styles.futureRow}>
                <span className={styles.futureLabel}>Expense</span>
                <span className={styles.futureValue}>
                  {objective.future_expense != null ? formatCurrency(objective.future_expense) : '—'}
                </span>
              </div>
            </div>
            {(objective.rem_future != null || objective.eem_future != null) && (
              <div className={styles.correctionFactors}>
                <div className={styles.correctionRow}>
                  <span className={styles.correctionLabel}>REM</span>
                  <span className={styles.correctionValue}>
                    {objective.rem_current?.toFixed(2) ?? '—'} → {objective.rem_future?.toFixed(2) ?? '—'}
                  </span>
                </div>
                <div className={styles.correctionRow}>
                  <span className={styles.correctionLabel}>EEM</span>
                  <span className={styles.correctionValue}>
                    {objective.eem_current?.toFixed(2) ?? '—'} → {objective.eem_future?.toFixed(2) ?? '—'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BridgeCardFinancialChart;
